import { Box, Card, CardContent, Container, Grid, InputAdornment, Typography } from "@mui/material";
import { CorePermissions } from "app/appPermissions";
import { MprcBarTitle, MprcChipChoice, MprcConfirmDialog, MprcContainer, MprcFormBarHeader, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { ButtonBack } from "components/buttons";
import { MprcFilterBoxContent, MprcFilterCount } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { IconAdd, IconSearch } from "components/icons";
import { UserPersonalInterestsResponse } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserTagInterestAutoComplete } from "views/foundation/user/components/UserTagInterestAutoComplete";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    userId: string;
    name: string;
    onClose: () => void;
}

////////////////////////////////////////////////////////////////////////////

export const EditUserPersonalInterest: React.FC<IProps> = (props) => {

    const { userId, name, onClose } = props;

    const { t } = useTranslation();
    const api = useCoreApi();
    const session = useSession();
    const { enqueueSnackbar } = useSnackbar();

    const [dialog, setDialog] = useState<any>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    const [viewData, setViewData] = useState<UserPersonalInterestsResponse[] | null>(null);

    const isSelfUser = userId == session.user.userId;

    const canAdd = session.permissionExplorer.hasOne([CorePermissions.TagsPersonalInterestCreate]);

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const handleAdd = (tagName: string) => {

        if (viewData?.find(p => p.tagName == tagName)) {
            enqueueSnackbar(t("User.Edit.PersonalInterest.TagAlreadyAddedMessage", { name: tagName }), { variant: "error" });
            return;
        }

        setSaving(true);
        api.v1.user.addPersonalInterest({ userId, tagName: tagName })
            .then(result => {
                if (result.data !== null) {
                    enqueueSnackbar(t("User.Edit.PersonalInterest.AddSuccessMessage"), { variant: "success" });
                    setLoading(true);
                }
                else {
                    enqueueSnackbar(t("User.Edit.PersonalInterest.AddFailMessage"), { variant: "error" });
                }
            })
            .catch(e => setViewError(e))
            .finally(() => setSaving(false));
    };

    // Ao remover...
    const handleDelete = (tagId: string) => {
        setSaving(true);
        api.v1.user.removePersonalInterest({ userId, tagId: tagId })
            .then((result) => {
                if (result.data === true) {
                    enqueueSnackbar(t("User.Edit.PersonalInterest.DeleteSuccessMessage"), { variant: "success" });
                    setLoading(true);
                }
                else
                    enqueueSnackbar(t("User.Edit.PersonalInterest.DeleteFailMessage"), { variant: "error" });
            })
            .catch(e => setViewError(e))
            .finally(() => setSaving(false));
    };

    ///
    const handleConfirmDelete = (item: UserPersonalInterestsResponse) => () => {
        setDialog(
            <MprcConfirmDialog
                open
                severity="danger"
                message={<span>{t("User.Edit.PersonalInterest.ConfirmDeleteMessage")}</span>}
                onClose={
                    (result) => {
                        if (result) {
                            handleDelete(item.tagId);
                        }
                        setDialog(null);
                    }
                }
            />
        );
    };

    ///
    const RenderFilter = (
        <MprcCardFilterBox>
            <MprcFilterBoxContent title={t("User.Edit.PersonalInterest.AddingTitle")}>
                <Grid container direction="column" spacing={1}>
                    <Grid>
                        <Typography variant="caption" component="p">
                            {t("User.Edit.PersonalInterest.AddingHintL1")}
                        </Typography>
                        <Typography variant="caption" component="p">
                            {t("User.Edit.PersonalInterest.AddingHintL2")}
                        </Typography>
                    </Grid>

                    {isSelfUser &&
                        <Grid>
                            <UserTagInterestAutoComplete
                                placeholder={canAdd ? t("User.Edit.PersonalInterest.PlaceholderText") : t("User.Edit.PersonalInterest.SearchPlaceholderText")}
                                multiSelect={false}
                                selectedItems={[]}
                                onChange={(items) => items.length > 0 && handleAdd(items[0].label)}
                                onNewTag={item => handleAdd(item.label)}
                                onError={e => setViewError(e)}
                                startAdorment={
                                    <InputAdornment position="start">
                                        {canAdd ? <IconAdd /> : <IconSearch />}
                                    </InputAdornment>
                                }
                            />
                        </Grid>
                    }
                </Grid>
            </MprcFilterBoxContent>
        </MprcCardFilterBox>
    );

    ///
    const renderGrid = () => (
        viewData?.map((item) => {
            return (
                <MprcChipChoice key={item.tagId} label={item.tagName} disabled={!isSelfUser} onDelete={isSelfUser ? handleConfirmDelete(item) : undefined} />
            );
        })
    );

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////////

    // Ao carregar...
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.user.getPersonalInterests({ userId });
                setViewData(result.data);
            }
            catch (e: any) {
                setViewData(null);
                setViewError(e as Error);
            }
            setLoading(false);
        }
        if (loading) getData();
    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <Box>

            {dialog}

            <MprcRequestIndicator
                visible={loading || saving}
                type={loading ? "request" : "post"}
            />

            <MprcRequestErrorDialog
                error={viewError}
                onClose={() => { setViewError(undefined); }}
            />

            <MprcContainer>
                <MprcFormBarHeader
                    actions={
                        [
                            <ButtonBack rounded size="large" variant="text" onClick={onClose} />,
                            <MprcBarTitle>{userId == session.user.userId ? t("User.Common.YourProfileText") : name}</MprcBarTitle>
                        ]
                    }
                />
            </MprcContainer>
            <Container maxWidth="lg">
                <Card sx={{ p: 0, mt: 0 }}>
                    {RenderFilter}
                    <CardContent>
                        <MprcContainer>
                            <MprcFilterCount
                                loading={loading}
                                isEmpty={(viewData?.length ?? 0) == 0}
                                countText={t("User.Edit.PersonalInterest.RecordsFoundText", { count: viewData?.length ?? 0 })}
                            />
                        </MprcContainer>
                        {
                            viewData && viewData.length > 0 &&
                            <MprcContainer>
                                {renderGrid()}
                            </MprcContainer>
                        }
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};
